import { useEffect, useMemo, useState } from 'react';
import {
    ColDef,
    type DefaultMenuItem,
    FirstDataRenderedEvent,
    GetContextMenuItems,
    GetMainMenuItemsParams,
    GridApi,
    IServerSideDatasource,
    ModelUpdatedEvent,
    RowDataUpdatedEvent,
    RowModelType,
    SizeColumnsToContentStrategy,
    themeBalham,
    type ToolPanelDef,
} from 'ag-grid-enterprise';
import type { AgGridReactProps } from 'ag-grid-react';
import { debounce } from 'lodash';

import { PlatformConfig } from '@blockworks/platform';
import { studioExecutionApi } from '@blockworks/platform/api/research/studio/execution';
import { isServerSide } from '@blockworks/platform/utils';
import { Bit } from '@blockworks/ui/bit';
import { Text } from '@blockworks/ui/text';

import { useVisualizationOptionsContext } from '../../../../../../context';
import { LoadingCell } from '../../loading-cell';
import { VisualizationTableProps } from '../../models';

import { useDatasource, UseVisualizationTableProps } from './use-datasource';

const ROW_MODEL_TYPE: RowModelType = 'serverSide';
const COLUMN_VISIBILITY_TAB: ToolPanelDef = {
    id: 'columns',
    labelDefault: 'Columns',
    labelKey: 'columns',
    iconKey: 'columns',
    toolPanel: 'agColumnsToolPanel',
    toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressSideButtons: true,
        suppressColumnFilter: true,
        suppressColumnSelectAll: true,
        suppressColumnExpandAll: true,
        suppressDragHandle: true,
    },
};
const DEFAULT_COL_DEF: ColDef = {
    flex: 1,
    resizable: false,
    sortable: false,
    filter: false,
    suppressMovable: true,
    loadingCellRenderer: LoadingCell,
};
const AUTOSIZE_STRATEGY: SizeColumnsToContentStrategy = { type: 'fitCellContents' };
const getContextMenuItems: GetContextMenuItems = () => ['copy', 'copyWithHeaders'];
const getSidebarToolPanels = (enableColumnVisibility?: boolean): ToolPanelDef[] => {
    const panels: ToolPanelDef[] = [];
    if (enableColumnVisibility) {
        panels.push(COLUMN_VISIBILITY_TAB);
    }
    return panels;
};

const EXCLUDED_MAIN_MENU_ITEMS = {
    autoSizeAll: true,
    autoSizeThis: true,
    pinSubMenu: true,
    columnChooser: true,
} as Record<DefaultMenuItem, boolean>;
const getMainMenuItems = (params: GetMainMenuItemsParams) =>
    params.defaultItems.filter(item => !EXCLUDED_MAIN_MENU_ITEMS[item]);

const AG_GRID_WRAPPER_HTML_ID = 'bw-table-visualization-wrapper';

const stylizeScrollbar = () => {
    if (isServerSide()) return;
    const gridElement = document.querySelector(`#${AG_GRID_WRAPPER_HTML_ID}`);
    const yScrollbar = gridElement?.querySelector('.ag-body-vertical-scroll-viewport') as HTMLElement;
    const xScrollbar = gridElement?.querySelector('.ag-body-horizontal-scroll-viewport') as HTMLElement;
    if (yScrollbar) {
        yScrollbar.classList.add('scroller');
    }
    if (xScrollbar) {
        xScrollbar.classList.add('scroller');
    }
};

const modifiedBalhamTheme = themeBalham.withParams({
    wrapperBorderRadius: '4px',
    headerColumnBorderHeight: '100%',
    headerColumnBorder: '1px solid var(--color-surface-selected)',
    headerTextColor: 'var(--color-content)',
    headerHeight: 24,
    headerBackgroundColor: 'var(--color-surface-muted)',

    borderColor: 'var(--color-surface-divider)',
    backgroundColor: 'transparent',
    rowHoverColor: 'var(--color-surface-muted)',
    oddRowBackgroundColor: 'transparent',
    rangeSelectionBackgroundColor: 'var(--color-primary-active)',
    rangeSelectionBorderColor: 'var(--color-primary-ring)',
    rangeSelectionHighlightColor: 'var(--color-primary-surface)',

    menuBorder: '1px solid var(--color-surface-divider)',
    menuBackgroundColor: 'var(--color-surface)',
    buttonHoverBackgroundColor: 'var(--color-surface-interact)',

    sideBarBackgroundColor: 'var(--color-surface-deselected)',
    sideBarPanelWidth: 175,
    sideButtonBarTopPadding: 0,
    sideButtonBorder: '1px solid var(--color-surface-divider)',
    sideButtonSelectedBorder: '1px solid var(--color-surface-divider)',
    sideButtonBackgroundColor: 'var(--color-surface-deselected)',
    sideButtonHoverBackgroundColor: 'var(--color-surface-interact)',
    sideButtonSelectedBackgroundColor: 'var(--color-surface-selected)',

    textColor: 'var(--color-content)',
    menuTextColor: 'var(--color-content)',
    cellTextColor: 'var(--color-content)',
    tabTextColor: 'var(--color-content)',
    subtleTextColor: 'var(--color-content-muted)',
    buttonTextColor: 'var(--color-content)',

    checkboxCheckedBackgroundColor: 'var(--color-primary)',
    checkboxCheckedShapeColor: 'var(--color-surface)',
    checkboxCheckedBorderColor: 'var(--color-primary)',
    checkboxUncheckedBorderColor: 'var(--color-surface-divider)',
    checkboxBorderRadius: '4px',
});

type UseAgGridOptionsProps = Partial<Pick<VisualizationTableProps, 'options'>> & UseVisualizationTableProps;

const Bold = ({ children }: { children: React.ReactNode }) => <Bit.span fontWeight="semibold">{children}</Bit.span>;
const ReadyOnlyPaginationStatusPanel = ({ api }: { api: GridApi }) => {
    const [currentPageRowCount, setCurrentPageRowCount] = useState(0);
    const [total, setTotal] = useState(api.paginationGetRowCount());

    useEffect(() => {
        const updatePagination = () => {
            const pageSize = api.paginationGetPageSize();
            const totalRows = api.paginationGetRowCount();
            const currentPage = api.paginationGetCurrentPage();
            const startRow = currentPage * pageSize;

            setCurrentPageRowCount(Math.min(pageSize, totalRows - startRow));
            setTotal(totalRows);
        };
        updatePagination();
        api.addEventListener('paginationChanged', updatePagination);
        return () => api.removeEventListener('paginationChanged', updatePagination);
    }, [api]);

    if (total === 0 || currentPageRowCount === 0) return null;
    return (
        <Text size="xs" color="muted" align="center">
            Showing <Bold>1</Bold>-<Bold>{currentPageRowCount}</Bold> of <Bold>{total}</Bold>
        </Text>
    );
};

const useAgGridOptions = ({
    options,
    data,
    meta,
    executionId,
    columnTypes,
    setColumnTypes,
}: UseAgGridOptionsProps): { agGridProps: AgGridReactProps; serverSideDatasource: IServerSideDatasource } => {
    const { features } = useVisualizationOptionsContext();
    const { tablePagination } = features;
    const {
        enableColumnVisibility,
        pageSize = studioExecutionApi.useGetExecution.models.DEFAULT_TABLE_EXECUTION_LIMIT,
    } = options ?? {};

    const updateColumnsMinWidth = useMemo(
        () =>
            debounce(({ api }: RowDataUpdatedEvent | FirstDataRenderedEvent | ModelUpdatedEvent) => {
                const allColumns = api.getColumns();
                if (!allColumns?.length) return;

                // Auto-size all columns to fit their content
                api.autoSizeAllColumns(false);

                // Update columnDefs with minWidth based on current width
                const updatedColumnDefs: ColDef[] = allColumns.map(col => {
                    return { ...col.getDefinition(), minWidth: col.getActualWidth() };
                });

                /**
                 * Then let's size the columns to fit the grid.
                 *
                 * The order here matters!
                 * `autoSizeAllColumns` runs before `sizeColumnsToFit` to prioritize content sizing,
                 * and set minWidth on the columns. Then we can stretch to fill the remaining available
                 * space in the grid.
                 **/
                api.setGridOption('columnDefs', updatedColumnDefs);
                api.sizeColumnsToFit();
            }, 10),
        [],
    );

    const agGridProps = useMemo<AgGridReactProps>(() => {
        const maybeSupressedPaginationControls = !!tablePagination
            ? {}
            : {
                  suppressPaginationPanel: !tablePagination,
                  statusBar: {
                      statusPanels: [
                          {
                              statusPanel: 'ReadyOnlyPaginationStatusPanel',
                              align: 'right',
                          },
                      ],
                  },
              };
        return {
            /** styles */
            onGridReady: stylizeScrollbar,
            onFirstDataRendered: updateColumnsMinWidth,
            onModelUpdated: updateColumnsMinWidth,
            onRowDataUpdated: updateColumnsMinWidth,
            overlayNoRowsTemplate: PlatformConfig.isProd
                ? 'Data not available. Try again later.'
                : 'No rows to display.',
            autoSizeStrategy: AUTOSIZE_STRATEGY,
            autoSizePadding: 10,
            defaultColDef: DEFAULT_COL_DEF,
            getMainMenuItems,
            theme: modifiedBalhamTheme,
            scrollbarWidth: 5,
            /** table features */
            getContextMenuItems,
            cellSelection: true,
            sideBar: {
                toolPanels: getSidebarToolPanels(enableColumnVisibility),
            },
            rowModelType: ROW_MODEL_TYPE,
            pagination: true,
            ...maybeSupressedPaginationControls,
            maxBlocksInCache: 5,
            cacheBlockSize: +pageSize,
            serverSideInitialRowCount: +pageSize,
            paginationPageSizeSelector: false,
            paginationPageSize: +pageSize,
            /** treeData: true, @todo implement tree data */
            /** suppressed table features */
            suppressCutToClipboard: true,
            suppressClipboardPaste: true,
            suppressExcelExport: true,
            suppressCsvExport: true,
            suppressRowHoverHighlight: true,
            suppressMultiSort: true,
            suppressMovableColumns: true,
            suppressRowDrag: true,
            suppressDragLeaveHidesColumns: true,
            suppressServerSideFullWidthLoadingRow: true,
            suppressColumnVirtualisation: true,
            components: {
                ReadyOnlyPaginationStatusPanel,
            },
        };
    }, [enableColumnVisibility, tablePagination, pageSize, updateColumnsMinWidth]);

    const serverSideDatasource = useDatasource({
        data,
        meta,
        executionId,
        columnTypes,
        setColumnTypes,
        options,
    });

    return { agGridProps, serverSideDatasource };
};

export { AG_GRID_WRAPPER_HTML_ID, useAgGridOptions };
